/**
 * MapTooltipToggle — shared toggle for the visibility of all Leaflet
 * tooltips on a map.
 *
 * b99.9.36 — re-architected from a CSS-class hack to a React Context.
 * The previous `opacity: 0` route hid tooltips but couldn't restore
 * them on hover, breaking the "I want them out of the way but still
 * peek on hover" UX the user asked for.
 *
 * How it works now:
 *   1. Each map renders a `<MapTooltipsContext.Provider value={visible}>`
 *      around its children.
 *   2. Markers render `<MapTooltip ...>` (a tiny wrapper) instead of
 *      `<Tooltip ...>`. The wrapper reads the context and:
 *         • visible=true  → `permanent={true}`  (tooltip always shown)
 *         • visible=false → `permanent={false}` (tooltip shows on hover only)
 *   3. Toggle button flips `visible`; React re-renders the affected
 *      Tooltip components with the new `permanent` flag. Leaflet
 *      handles the rest.
 *
 * The default context value is `true` so any `<MapTooltip>` used
 * outside a provider behaves exactly like a permanent tooltip — no
 * surprises for legacy call sites.
 */
import { createContext, useCallback, useContext, useState } from "react";
import { Tooltip } from "react-leaflet";
import { MessageSquare, MessageSquareOff } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MapTooltipsContext = createContext(true);

/**
 * Hook returning `{ visible, toggle, setVisible }`. Each map keeps its
 * own instance so toggling one doesn't affect another (e.g. trip
 * overview vs. the per-segment edit map below it).
 *
 * b99.9.65 — also expose `setVisible` so callers can sync the toggle
 * to other UI state (e.g. fullscreen mode defaulting to ON while
 * inline defaults to OFF).
 */
export function useTooltipToggle(defaultVisible = true) {
  const [visible, setVisible] = useState(defaultVisible);
  const toggle = useCallback(() => setVisible((v) => !v), []);
  return { visible, toggle, setVisible };
}

/**
 * Drop-in replacement for `<Tooltip>` that respects the active
 * MapTooltipsContext. Pass `permanent={true}` (the default) to make
 * the tooltip honour the context; pass `permanent={false}` to force
 * a hover-only tooltip regardless of the toggle.
 *
 * b99.9.65 — accept `alwaysVisible` so specific tooltips can opt OUT
 * of the global hide. Used for trip-level start/finish in
 * `TripOverviewMap` so those labels remain visible even when the
 * toggle is OFF (intermediate stops are the only thing the user
 * wants to dim).
 */
export function MapTooltip({ permanent = true, alwaysVisible = false, ...rest }) {
  const visible = useContext(MapTooltipsContext);
  // If the call site asked for a permanent tooltip, defer to the
  // map-wide visibility flag (or `alwaysVisible` short-circuit).
  // Hover-only call sites (permanent=false) are honoured as-is.
  const finalPermanent = permanent && (alwaysVisible || visible);
  // react-leaflet's <Tooltip> binds `permanent` to the underlying
  // Leaflet tooltip ONCE at mount time and doesn't propagate later
  // prop changes (b99.9.37 root-cause). Force a remount by keying
  // on the final value so toggling the context actually flips the
  // tooltip's permanent state.
  return (
    <Tooltip
      key={finalPermanent ? "permanent" : "hover"}
      permanent={finalPermanent}
      {...rest}
    />
  );
}

/**
 * Pre-styled button component for floating top-right map clusters.
 * Pair with `useTooltipToggle()` in the parent.
 */
export default function MapTooltipToggle({
  visible,
  onClick,
  className = "",
  size = "sm",
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={onClick}
      className={`bg-background/90 backdrop-blur-sm ${className}`}
      title={visible ? "Esconder etiquetas" : "Mostrar etiquetas"}
      aria-label={visible ? "Esconder etiquetas" : "Mostrar etiquetas"}
      data-testid="map-tooltip-toggle"
      data-state={visible ? "on" : "off"}
    >
      {visible ? (
        <MessageSquare className="h-4 w-4" />
      ) : (
        <MessageSquareOff className="h-4 w-4 text-muted-foreground" />
      )}
    </Button>
  );
}
